import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { type Observable } from 'rxjs';

import {
  AskPredefinedTemplatesActions,
  AskPredefinedTemplatesSelectors,
  AskTemplatesActions,
  AskTemplatesSelectors,
} from '@core/ask-hr-templates-data';
import type {
  AskTemplate,
  CreateAskTemplate,
  UpdateAskTemplate,
} from '@core/ask-hr-templates-types';
import { createActionResponseFactory } from '@core/ngrx-legacy';
import { generateUuid } from '@core/utils-legacy';

@Injectable({ providedIn: 'root' })
export class AskTemplatesDataService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly templateActionResponse = createActionResponseFactory(AskTemplatesActions);

  public readonly templateCreated$ = this.actions$.pipe(
    ofType(AskTemplatesActions.createOnePessimisticSuccess),
  );
  public readonly templateUpdated$ = this.actions$.pipe(
    ofType(AskTemplatesActions.updateOnePessimisticSuccess),
  );
  public readonly templateDuplicated$ = this.actions$.pipe(
    ofType(AskTemplatesActions.duplicateTemplateSuccess),
  );
  public readonly templateDeleted$ = this.actions$.pipe(
    ofType(AskTemplatesActions.removeOnePessimisticSuccess),
  );

  public predefinedTemplates = this.store.selectSignal(
    AskPredefinedTemplatesSelectors.entities({ scope: 'predefined' }),
  );

  public loadPredefinedTemplates(): void {
    this.store.dispatch(AskPredefinedTemplatesActions.fetchPredefined());
  }

  public createTemplate(template: CreateAskTemplate) {
    return this.templateActionResponse(
      AskTemplatesActions.createOnePessimistic,
      { payload: { data: template, correlationId: generateUuid() } },
      {
        success: AskTemplatesActions.createOnePessimisticSuccess,
        failed: AskTemplatesActions.createOnePessimisticFailed,
        successResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.byId(payload.data.id)),
        failedResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.error(payload.correlationId)),
      },
    );
  }

  public duplicateTemplate(template: CreateAskTemplate) {
    return this.templateActionResponse(
      AskTemplatesActions.duplicateTemplate,
      { payload: { data: template, correlationId: generateUuid() } },
      {
        success: AskTemplatesActions.duplicateTemplateSuccess,
        failed: AskTemplatesActions.duplicateTemplateFailed,
        successResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.byId(payload.data.id)),
        failedResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.error(payload.correlationId)),
      },
    );
  }

  public updateTemplate(template: UpdateAskTemplate) {
    return this.templateActionResponse(
      AskTemplatesActions.updateOnePessimistic,
      { payload: { data: template } },
      {
        success: AskTemplatesActions.updateOnePessimisticSuccess,
        failed: AskTemplatesActions.updateOnePessimisticFailed,
        successResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.byId(payload.data.id)),
        failedResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.error(payload.data.id)),
      },
    );
  }

  public deleteTemplate(id: number) {
    return this.templateActionResponse(
      AskTemplatesActions.removeOnePessimistic,
      { payload: { data: id } },
      {
        success: AskTemplatesActions.removeOnePessimisticSuccess,
        failed: AskTemplatesActions.removeOnePessimisticFailed,
      },
    );
  }

  public loadTemplateById(id: number) {
    return this.templateActionResponse(
      AskTemplatesActions.fetchOne,
      { payload: { id } },
      {
        success: AskTemplatesActions.fetchOneSuccess,
        failed: AskTemplatesActions.fetchOneFailed,
        successResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.byId(payload.data.id)),
        failedResponse: ({ store, action: { payload } }) =>
          store.select(AskTemplatesSelectors.error(payload.id)),
      },
    );
  }

  public getTemplateById(id: number): Observable<AskTemplate | null> {
    return this.store.select(AskTemplatesSelectors.byId(id));
  }
}
